import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Send, Lock, User, Store, Package } from "lucide-react";

export default function Contact() {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [replies, setReplies] = useState({});
  const [sendingId, setSendingId] = useState(null);

  const loadConvos = () => {
    if (!user) return;
    api.get("/messages/me").then((r) => setConversations(r.data));
  };

  useEffect(() => { loadConvos(); /* eslint-disable-next-line */ }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setLoading(true);
    try {
      await api.post("/messages", { type: "contact", message });
      toast.success("Message envoyé. Vous recevrez une réponse ici.");
      setMessage("");
      loadConvos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (id) => {
    const text = (replies[id] || "").trim();
    if (!text) return;
    setSendingId(id);
    try {
      await api.post(`/messages/${id}/reply`, { message: text });
      setReplies((r) => ({ ...r, [id]: "" }));
      loadConvos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSendingId(null);
    }
  };

  const markSeen = async (id) => {
    try { await api.put(`/messages/${id}/seen`); loadConvos(); } catch (e) {}
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="contact-page">
      <div className="fade-up">
        <div className="label-eyebrow mb-3">Contact</div>
        <h1 className="font-display text-5xl mb-3">Une question ?</h1>
        <p className="text-[#a3a8a4] mb-10">Écrivez à la boutique. Vos échanges apparaissent en dessous.</p>
      </div>

      {user ? (
        <>
          <form onSubmit={submit} className="glass p-8 space-y-5 fade-up-delay-1" data-testid="contact-form">
            <label className="block">
              <span className="label-eyebrow block mb-2">Nouveau message</span>
              <textarea
                value={message}
                onChange={(e)=>setMessage(e.target.value)}
                required
                rows={5}
                placeholder="Bonjour, j'aimerais savoir si vous avez..."
                className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-4 py-3 focus:border-[#d4af37] focus:outline-none"
                data-testid="contact-message"
              />
            </label>
            <button disabled={loading} className="w-full bg-[#d4af37] text-black px-6 py-3.5 font-medium tracking-widest uppercase text-xs hover:bg-[#e6c27a] inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit">
              <Send size={14}/> {loading ? "Envoi..." : "Envoyer"}
            </button>
          </form>

          {conversations.length > 0 && (
            <div className="mt-12 fade-up-delay-2">
              <div className="label-eyebrow mb-2">Historique</div>
              <h2 className="font-display text-3xl mb-6">Mes conversations</h2>
              <div className="space-y-4">
                {conversations.map((m) => (
                  <div key={m.id} className={`border ${m.client_unread > 0 ? "border-[#d4af37]/40 bg-[#1a241b]/20" : "border-[rgba(253,251,247,0.08)]"} p-6`} data-testid={`convo-${m.id}`} onMouseEnter={() => m.client_unread > 0 && markSeen(m.id)}>
                    <div className="flex items-center gap-2 flex-wrap mb-4">
                      <span className="label-eyebrow">{m.type === "reservation" ? "Réservation" : "Message"}</span>
                      {m.product_name && (
                        <span className="text-xs text-[#a3a8a4] inline-flex items-center gap-1"><Package size={12}/> {m.product_name}</span>
                      )}
                      {m.client_unread > 0 && <span className="text-xs gold-text">● {m.client_unread} nouvelle{m.client_unread > 1 ? "s" : ""} réponse{m.client_unread > 1 ? "s" : ""}</span>}
                    </div>

                    <div className="space-y-3 border-l-2 border-[rgba(253,251,247,0.08)] pl-4 mb-4">
                      <Bubble from="client" name={m.user_firstName} text={m.message} at={m.created_at}/>
                      {(m.replies || []).map((r) => (
                        <Bubble key={r.id} from={r.from} name={r.from === "admin" ? "Noctalis" : m.user_firstName} text={r.message} at={r.at}/>
                      ))}
                    </div>

                    <div className="space-y-2">
                      <textarea
                        value={replies[m.id] || ""}
                        onChange={(e) => setReplies((r) => ({ ...r, [m.id]: e.target.value }))}
                        rows={2}
                        placeholder="Écrire une réponse..."
                        className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none"
                        data-testid={`convo-reply-${m.id}`}
                      />
                      <button
                        onClick={() => sendReply(m.id)}
                        disabled={sendingId === m.id || !(replies[m.id] || "").trim()}
                        className="bg-[#d4af37] text-black px-5 py-2 text-xs uppercase tracking-widest hover:bg-[#e6c27a] inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid={`convo-send-${m.id}`}
                      >
                        <Send size={14}/> {sendingId === m.id ? "Envoi..." : "Répondre"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="glass p-10 fade-up-delay-1 text-center space-y-6" data-testid="contact-login-required">
          <Lock className="mx-auto gold-text" size={32}/>
          <div>
            <div className="font-display text-2xl mb-2">Un compte est requis</div>
            <p className="text-sm text-[#a3a8a4]">Connectez-vous ou créez un compte gratuit pour nous écrire. Ça nous permet de vous recontacter directement.</p>
          </div>
          <div className="flex gap-3 justify-center">
            <Link to="/login" className="bg-[#d4af37] text-black px-6 py-3 text-xs uppercase tracking-widest hover:bg-[#e6c27a]" data-testid="contact-login">Se connecter</Link>
            <Link to="/register" className="border border-[#d4af37] text-[#d4af37] px-6 py-3 text-xs uppercase tracking-widest hover:bg-[#d4af37]/10" data-testid="contact-register">Créer un compte</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Bubble({ from, name, text, at }) {
  const isAdmin = from === "admin";
  return (
    <div className={`flex gap-3 ${isAdmin ? "justify-start" : "justify-end"}`}>
      {isAdmin && (
        <div className="w-8 h-8 rounded-full bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0"><Store size={14}/></div>
      )}
      <div className={`max-w-[80%] px-4 py-3 ${isAdmin ? "bg-[#0a0e0b] border border-[#d4af37]/40" : "bg-[#d4af37] text-black"}`}>
        <div className={`text-[10px] uppercase tracking-widest mb-1 ${isAdmin ? "gold-text" : "text-black/60"}`}>
          {isAdmin ? name : "Vous"} · {new Date(at).toLocaleString("fr-FR")}
        </div>
        <div className="text-sm whitespace-pre-wrap">{text}</div>
      </div>
      {!isAdmin && (
        <div className="w-8 h-8 rounded-full bg-[#1a241b] flex items-center justify-center flex-shrink-0"><User size={14}/></div>
      )}
    </div>
  );
}
